import React, { useEffect, useState } from "react";
import { Truck, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money, num, Spinner, PageHeader, Empty } from "@/components/shared";
import { FieldW, SelectBox } from "@/pages/Clients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Purchases() {
  const [list, setList] = useState(null);
  const [ings, setIngs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/purchases").then((r) => setList(r.data));
    api.get("/ingredients").then((r) => setIngs(r.data));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm({ ingredient_id: ings[0]?.id, supplier: "", quantity: 0, internal_rate: 0, invoice_ref: "", date: "", expiry_date: "", notes: "" }); setOpen(true); };

  const total = (Number(form.quantity) || 0) * (Number(form.internal_rate) || 0);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/purchases?confirm=true", { ...form, quantity: Number(form.quantity), internal_rate: Number(form.internal_rate), date: form.date || undefined, expiry_date: form.expiry_date || null });
      toast.success("Purchase confirmed — stock & cost updated");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!list) return <Spinner />;

  return (
    <div>
      <PageHeader title="Purchases" subtitle="Manual purchase entry · confirming updates stock and weighted-average cost">
        <Button onClick={openNew} data-testid="add-purchase-btn" className="gap-1.5 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">
          <Plus className="h-4 w-4" /> New Purchase
        </Button>
      </PageHeader>

      {list.length === 0 ? <Empty icon={Truck} title="No purchases recorded" /> : (
        <div className="card-surface overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent border-border">
              <TableHead>Date</TableHead><TableHead>Ingredient</TableHead><TableHead>Supplier</TableHead>
              <TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead>Invoice</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((p) => (
                <TableRow key={p.id} className="border-border" data-testid={`purchase-row-${p.id}`}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{p.date}</TableCell>
                  <TableCell className="text-white font-medium">{p.ingredient_name}</TableCell>
                  <TableCell className="text-slate-300">{p.supplier || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-slate-200">{num(p.quantity)}</TableCell>
                  <TableCell className="text-right font-mono text-slate-300">{money(p.internal_rate)}</TableCell>
                  <TableCell className="text-right font-mono text-amber-300">{money(p.total)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{p.invoice_ref || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-card border-border">
          <DialogHeader><DialogTitle>New Purchase</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <FieldW label="Ingredient" full><SelectBox value={form.ingredient_id} onChange={(v) => set("ingredient_id", v)} options={ings.map((i) => ({ value: i.id, label: `${i.name} (${i.purchase_unit})` }))} testid="purchase-ingredient" /></FieldW>
            <FieldW label="Supplier"><Input value={form.supplier} onChange={(e) => set("supplier", e.target.value)} /></FieldW>
            <FieldW label="Purchase date"><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></FieldW>
            <FieldW label="Quantity"><Input type="number" data-testid="purchase-qty" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} /></FieldW>
            <FieldW label="Internal rate (AED / unit)"><Input type="number" data-testid="purchase-rate" value={form.internal_rate} onChange={(e) => set("internal_rate", e.target.value)} /></FieldW>
            <FieldW label="Invoice / reference"><Input value={form.invoice_ref} onChange={(e) => set("invoice_ref", e.target.value)} /></FieldW>
            <FieldW label="Expiry date"><Input type="date" value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} /></FieldW>
            <FieldW label="Notes" full><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></FieldW>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-secondary/40 border border-border px-4 py-3">
            <span className="text-sm text-muted-foreground">Total</span>
            <span className="font-mono font-bold text-amber-300 text-lg" data-testid="purchase-total">{money(total)}</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !form.ingredient_id} data-testid="save-purchase-btn" className="bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">Confirm Purchase</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
